import { useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DownloadIcon from "@mui/icons-material/Download";
import DashboardLayout from "../components/DashboardLayout";

const ACCEPTED_TYPES = [".csv", "text/csv", "application/vnd.ms-excel"];
const REQUIRED_HEADERS = ["customer", "sku", "price", "effective_from"];

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\uFEFF/g, "")
    .replace(/[\s-]+/g, "_");

const parseCsvLine = (line) => {
  const values = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') { current += '"'; i += 1; }
      else { inQuotes = !inQuotes; }
      continue;
    }
    if (char === "," && !inQuotes) { values.push(current.trim()); current = ""; continue; }
    current += char;
  }
  values.push(current.trim());
  return values;
};

const isValidDate = (value) =>
  !!value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !isNaN(Date.parse(value));

const parseCsvText = (text) => {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) throw new Error("CSV file is empty.");
  if (lines.length < 2) throw new Error("CSV must include a header row and at least one data row.");

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
  if (missing.length > 0)
    throw new Error(`Missing required column(s): ${missing.join(", ")}. Expected: ${REQUIRED_HEADERS.join(", ")}.`);

  const ci = headers.indexOf("customer");
  const si = headers.indexOf("sku");
  const pi = headers.indexOf("price");
  const ei = headers.indexOf("effective_from");

  const rows = [];
  const errors = [];

  lines.slice(1).forEach((line, idx) => {
    const rn = idx + 2;
    const v = parseCsvLine(line);
    const customer = String(v[ci] ?? "").trim();
    const sku = String(v[si] ?? "").trim();
    const priceRaw = String(v[pi] ?? "").trim();
    const effective_from = String(v[ei] ?? "").trim();
    let bad = false;
    if (!customer) { errors.push(`Row ${rn}: "customer" is empty.`); bad = true; }
    if (!sku) { errors.push(`Row ${rn}: "sku" is empty.`); bad = true; }
    if (!priceRaw) { errors.push(`Row ${rn}: "price" is empty.`); bad = true; }
    else if (!Number.isFinite(Number(priceRaw))) { errors.push(`Row ${rn}: "price" must be numeric (got "${priceRaw}").`); bad = true; }
    if (!effective_from) { errors.push(`Row ${rn}: "effective_from" is empty.`); bad = true; }
    else if (!isValidDate(effective_from)) { errors.push(`Row ${rn}: "effective_from" must be YYYY-MM-DD (got "${effective_from}").`); bad = true; }
    if (!bad) rows.push({ customer, sku, price: Number(priceRaw), effective_from });
  });

  if (rows.length === 0) throw new Error("No valid rows found. Fix the errors listed above and re-upload.");
  return { rows, errors };
};

// ── API base URL resolution ───────────────────────────────────────────────────
// In LOCAL DEV  → use the Vite proxy path /pricing-api
//   Browser calls /pricing-api/token (same-origin, no CORS)
//   Vite strips /pricing-api and forwards to https://wilsondb-git-582459784765.us-east5.run.app
// In PRODUCTION → use VITE_PRICING_API_URL directly (backend must have CORS configured)
const IS_DEV = import.meta.env.DEV;
const PRICING_API_BASE = IS_DEV
  ? '/pricing-api'
  : (import.meta.env.VITE_PRICING_API_URL || '').replace(/\/+$/, '');

const PRICING_API_USERNAME = import.meta.env.VITE_PRICING_API_USERNAME || '';
const PRICING_API_PASSWORD = import.meta.env.VITE_PRICING_API_PASSWORD || '';

let _cachedToken = null;
let _tokenExpiresAt = 0;

const getPricingApiToken = async () => {
  if (_cachedToken && Date.now() < _tokenExpiresAt) return _cachedToken;

  if (!PRICING_API_USERNAME || !PRICING_API_PASSWORD)
    throw new Error(
      'Pricing API credentials not configured. Set VITE_PRICING_API_USERNAME and VITE_PRICING_API_PASSWORD in your .env file.'
    );

  const loginUrl = `${PRICING_API_BASE}/token`;
  console.log('[PricingAPI] Token request URL:', loginUrl);

  const form = new URLSearchParams();
  form.append('username', PRICING_API_USERNAME);
  form.append('password', PRICING_API_PASSWORD);
  form.append('grant_type', 'password');

  const res = await fetch(loginUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  });

  console.log('[PricingAPI] Token response status:', res.status);

  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(b?.detail || b?.message || `Authentication failed (HTTP ${res.status}).`);
  }

  const data = await res.json();
  _cachedToken = data.access_token;
  _tokenExpiresAt = Date.now() + ((data.expires_in ?? 3300) * 1000);
  console.log('[PricingAPI] Token received:', !!_cachedToken);

  if (!_cachedToken) throw new Error('Authentication response did not contain an access_token.');
  return _cachedToken;
};

const uploadPricingCsv = async (file, dryRun) => {
  const token = await getPricingApiToken();

  // dry_run as query param (per OpenAPI spec) + also in FormData (matching Postman)
  const uploadUrl = `${PRICING_API_BASE}/pricing-changes?dry_run=${dryRun ? 'true' : 'false'}`;
  console.log('[PricingAPI] Upload URL:', uploadUrl);

  const fd = new FormData();
  fd.append('file', file, file.name);
  fd.append('dry_run', dryRun ? 'true' : 'false');
  console.log('[PricingAPI] FormData keys:', [...fd.keys()]);

  const res = await fetch(uploadUrl, {
    method: 'POST',
    // Do NOT set Content-Type — browser sets multipart/form-data + boundary automatically
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail =
      typeof body?.detail === 'string' ? body.detail
      : Array.isArray(body?.detail) ? body.detail.map((d) => d?.msg || JSON.stringify(d)).join('; ')
      : body?.message || `HTTP ${res.status}: ${res.statusText}`;
    const err = new Error(detail);
    err.status = res.status;
    err.responseBody = body;

    throw err;
  }
  return { status: res.status, data: body };
};

const MarketplacePriceUpdate = () => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [submitResult, setSubmitResult] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const closeSnackbar = useCallback(() => setSnackbar((p) => ({ ...p, open: false })), []);
  const showSnackbar = useCallback((message, severity = "success") => setSnackbar({ open: true, message, severity }), []);

  const previewRows = useMemo(
    () => parsedRows.map((row, i) => ({ ...row, _id: `${row.customer}-${row.sku}-${i}` })),
    [parsedRows]
  );

  const resetSelection = useCallback(() => {
    setFile(null); setParsedRows([]); setParseWarnings([]); setSubmitResult(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const loadFile = useCallback(async (selectedFile) => {
    if (!selectedFile) return;
    const fn = selectedFile.name.toLowerCase();
    const isCsv = fn.endsWith(".csv") || ACCEPTED_TYPES.includes(selectedFile.type) || !selectedFile.type;
    if (!isCsv) { showSnackbar("Please upload a .csv file.", "warning"); return; }
    try {
      const text = await selectedFile.text();
      const { rows, errors } = parseCsvText(text);
      setFile(selectedFile); setParsedRows(rows); setParseWarnings(errors); setSubmitResult(null);
      showSnackbar(
        `Loaded ${rows.length} valid row(s) from "${selectedFile.name}".` + (errors.length ? ` ${errors.length} row(s) skipped.` : ""),
        errors.length ? "warning" : "success"
      );
    } catch (err) {
      resetSelection();
      showSnackbar(err?.message || "Failed to parse CSV.", "error");
    }
  }, [resetSelection, showSnackbar]);

  const handleInputChange = useCallback((e) => {
    const f = e.target.files?.[0]; if (f) loadFile(f); e.target.value = "";
  }, [loadFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const f = e.dataTransfer?.files?.[0]; if (f) loadFile(f);
  }, [loadFile]);

  const handleDownloadSample = useCallback(() => {
    const csv = ["customer,sku,price,effective_from","AMAZON,SKU001,25.50,2026-09-18","WALMART,SKU002,30.00,2026-09-18"].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "marketplace-price-sample.csv" });
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }, []);

  const handleSubmit = async () => {
    if (!file || !parsedRows.length) { showSnackbar("Please upload a valid CSV before submitting.", "warning"); return; }
    if (isSubmitting) return;
    setIsSubmitting(true); setSubmitResult(null);
    try {
      const { status, data } = await uploadPricingCsv(file, dryRun);
      const processed = data?.processed_records ?? data?.total_processed ?? data?.processed ?? null;
      const updated = data?.updated_records ?? data?.total_updated ?? data?.updated ?? null;
      const msg = data?.message || data?.detail || (status >= 200 && status < 300 ? "Upload completed." : "Upload failed.");
      const apiErrors = data?.errors || data?.row_errors || data?.validation_errors || [];
      const apiWarnings = data?.warnings || [];
      const rs = Array.isArray(apiErrors) && apiErrors.length > 0 ? "warning" : "success";
      setSubmitResult({ status: rs, message: msg, processed, updated, errors: Array.isArray(apiErrors) ? apiErrors : [], warnings: Array.isArray(apiWarnings) ? apiWarnings : [], dryRun });
      showSnackbar(dryRun ? `Dry run done: ${msg}` : `Upload successful: ${msg}`, rs);
    } catch (err) {
      let userMsg = err?.message || "An unexpected error occurred.";
      if (err?.status === 401) userMsg = "Authentication failed. Check your API credentials in environment config.";
      else if (err?.status === 403) userMsg = "Forbidden: You do not have permission to upload prices.";
      else if (err?.status === 404) userMsg = "Pricing API endpoint not found. Verify VITE_PRICING_API_URL.";
      else if (err?.status === 422) userMsg = `Validation Error: ${err.message}`;
      else if (err?.status >= 500) userMsg = `Server Error (${err.status}): ${err.message}`;
      else if (!err?.status) userMsg = `Network Error: ${err.message}`;
      const apiErrors = err?.responseBody?.detail
        ? Array.isArray(err.responseBody.detail) ? err.responseBody.detail.map((d) => d?.msg || JSON.stringify(d)) : [String(err.responseBody.detail)]
        : [];
      setSubmitResult({ status: "error", message: userMsg, processed: null, updated: null, errors: apiErrors, warnings: [], dryRun });
      showSnackbar(userMsg, "error");
    } finally { setIsSubmitting(false); }
  };

  const fileSizeLabel = file
    ? file.size >= 1024 * 1024 ? `${(file.size / 1048576).toFixed(2)} MB` : `${(file.size / 1024).toFixed(1)} KB`
    : "";

  return (
    <DashboardLayout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main", mb: 0.5 }}>
          Marketplace Price Update
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
          Upload a CSV containing marketplace SKU prices. Data is sent to the pricing API which
          updates the GCP database. Enable <strong>Dry Run</strong> to validate without making real
          changes.
        </Typography>

        <Alert severity="info" sx={{ mb: 2, maxWidth: 760 }}>
          Required CSV columns: <strong>customer, sku, price, effective_from</strong>&nbsp;&mdash;&nbsp;
          Date format: <strong>YYYY-MM-DD</strong>
        </Alert>

        {submitResult && (
          <Alert
            severity={submitResult.status}
            icon={submitResult.status === "success" ? <CheckCircleOutlineIcon /> : submitResult.status === "warning" ? <WarningAmberIcon /> : <ErrorOutlineIcon />}
            sx={{ mb: 2, maxWidth: 760 }}
            onClose={() => setSubmitResult(null)}
          >
            <Box>
              {submitResult.dryRun && <Chip label="DRY RUN" size="small" color="info" sx={{ mb: 0.5, mr: 1 }} />}
              <Typography variant="body2" sx={{ fontWeight: 600, display: "inline" }}>{submitResult.message}</Typography>
              {(submitResult.processed !== null || submitResult.updated !== null) && (
                <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
                  {submitResult.processed !== null && <>Processed: <strong>{submitResult.processed}</strong>&nbsp;</>}
                  {submitResult.updated !== null && <>Updated: <strong>{submitResult.updated}</strong></>}
                </Typography>
              )}
              {submitResult.warnings.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Warnings:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {submitResult.warnings.slice(0, 10).map((w, i) => (
                      <li key={i}><Typography variant="caption">{typeof w === "string" ? w : JSON.stringify(w)}</Typography></li>
                    ))}
                    {submitResult.warnings.length > 10 && <Typography variant="caption" color="text.secondary">…and {submitResult.warnings.length - 10} more</Typography>}
                  </Box>
                </Box>
              )}
              {submitResult.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Row Errors:</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {submitResult.errors.slice(0, 10).map((e, i) => (
                      <li key={i}><Typography variant="caption">{typeof e === "string" ? e : JSON.stringify(e)}</Typography></li>
                    ))}
                    {submitResult.errors.length > 10 && <Typography variant="caption" color="text.secondary">…and {submitResult.errors.length - 10} more</Typography>}
                  </Box>
                </Box>
              )}
            </Box>
          </Alert>
        )}

        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadSample} sx={{ mb: 2 }}>
          Download Sample CSV
        </Button>

        <Paper
          elevation={2}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          sx={{
            p: 3, mb: 2, border: "2px dashed", textAlign: "center", maxWidth: 760, cursor: "pointer",
            borderColor: isDragging ? "primary.main" : "grey.400",
            backgroundColor: isDragging ? "rgba(25,118,210,0.04)" : "background.paper",
            transition: "border-color 0.2s, background-color 0.2s",
          }}
        >
          <UploadFileIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 0.5 }}>Drag and drop a CSV file here</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>or click to browse</Typography>
          <input ref={inputRef} type="file" accept=".csv,text/csv" style={{ display: "none" }} onChange={handleInputChange} onClick={(e) => e.stopPropagation()} />
          <Button variant="contained" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }} disabled={isSubmitting}>Browse CSV</Button>
          {file && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }} onClick={(e) => e.stopPropagation()}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{file.name}</Typography>
              {fileSizeLabel && <Typography variant="caption" color="text.secondary">({fileSizeLabel})</Typography>}
              <Tooltip title="Remove file"><IconButton size="small" onClick={resetSelection} disabled={isSubmitting}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Box>
          )}
        </Paper>

        {parseWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, maxWidth: 760 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{parseWarnings.length} row(s) skipped due to errors:</Typography>
            <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
              {parseWarnings.slice(0, 8).map((w) => (<li key={w}><Typography variant="body2">{w}</Typography></li>))}
            </Box>
            {parseWarnings.length > 8 && <Typography variant="caption" color="text.secondary">…and {parseWarnings.length - 8} more</Typography>}
          </Alert>
        )}

        {previewRows.length > 0 && (
          <Paper elevation={1} sx={{ mb: 2, maxWidth: 760 }}>
            <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              <Typography variant="h6">
                Preview&nbsp;<Typography component="span" variant="body2" color="text.secondary">({previewRows.length} valid row{previewRows.length !== 1 ? "s" : ""})</Typography>
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <FormControlLabel
                  control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={isSubmitting} color="warning" size="small" />}
                  label={<Typography variant="body2" sx={{ fontWeight: dryRun ? 600 : 400 }}>Dry Run</Typography>}
                />
                <Button
                  variant="contained"
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  color={dryRun ? "warning" : "primary"}
                >
                  {isSubmitting ? "Uploading\u2026" : dryRun ? "Upload Prices (Dry Run)" : "Upload Prices"}
                </Button>
              </Box>
            </Box>
            {dryRun && (
              <Alert severity="warning" icon={false} sx={{ mx: 2, mb: 1, py: 0.5 }}>
                <Typography variant="caption"><strong>Dry Run enabled</strong> &mdash; the API will validate your data but will <strong>not</strong> update the database.</Typography>
              </Alert>
            )}
            <Divider />
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Effective From</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row, i) => (
                    <TableRow key={row._id} hover>
                      <TableCell sx={{ color: "text.secondary" }}>{i + 1}</TableCell>
                      <TableCell>{row.customer}</TableCell>
                      <TableCell>{row.sku}</TableCell>
                      <TableCell align="right">{row.price.toFixed(2)}</TableCell>
                      <TableCell>{row.effective_from}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={7000} onClose={closeSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default MarketplacePriceUpdate;
